import { Component, computed, DestroyRef, inject, OnInit, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EMPTY, Subject } from 'rxjs';
import { catchError, switchMap, take } from 'rxjs/operators';
import { Router } from '@angular/router';
import {
  YdsButtonComponent,
  YdsCardComponent,
  YdsEmptyStatesComponent,
  YdsSpinnerComponent,
  YdsTableComponent,
  YdsSplitButtonComponent,
  YdsTableCellDirective,
  YdsPaginationComponent,
  type YdsTableColumn,
  type YdsSplitButtonItem,
} from '../../../yds-angular';
import { formatDisplayDate } from '../../../shared/general-ledger-yds.utils';
import { BookingGroup } from '../../../model/booking-group.model';
import { BookingGroupService } from '../bookinggroup.service';
import { ConfirmDialogService } from '../../../dialog/confirm-dialog/confirm-dialog.service';
import { deleteBookingGroupWithChecks } from '../bookinggroup-shared';

@Component({
  selector: 'app-bookinggroup-list',
  imports: [
    YdsCardComponent,
    YdsButtonComponent,
    YdsTableComponent,
    YdsSpinnerComponent,
    YdsEmptyStatesComponent,
    YdsSplitButtonComponent,
    YdsTableCellDirective,
    YdsPaginationComponent,
  ],
  templateUrl: './bookinggroup-list.component.html',
  styleUrl: './bookinggroup-list.component.scss',
})
export class BookinggroupListComponent implements OnInit {
  readonly pageSize = 10;
  readonly tableColumns: YdsTableColumn[] = [
    { key: 'bookingGroup_name', header: 'Boekingsgroep' },
    { key: 'generalLedger_name', header: 'Grootboek' },
    { key: 'startDate', header: 'Begindatum' },
    { key: 'endDate', header: 'Einddatum' },
    { key: 'actions', header: 'Actieknop', width: '160px' },
  ];

  readonly splitButtonItems: readonly YdsSplitButtonItem[] = [
    { id: 'edit', label: 'Wijzigen' },
    { id: 'bookingdefinition', label: 'Boekingsdefinitie' },
    { id: 'bookingrules', label: 'Boekingsregels' },
    { id: 'copy', label: 'Kopiëren', disabled: true },
    { id: 'delete', label: 'Verwijderen' },
  ];

  private router = inject(Router);
  private bookingGroupService = inject(BookingGroupService);
  private dialogService = inject(ConfirmDialogService);
  private destroyRef = inject(DestroyRef);

  bookingGroups: WritableSignal<BookingGroup[]> = signal([]);
  pageIndex = signal(0);
  isLoading = signal(true);

  private reload$ = new Subject<void>();

  pagedGroups = computed(() => {
    const start = this.pageIndex() * this.pageSize;
    return this.bookingGroups().slice(start, start + this.pageSize);
  });

  tableItems = computed(() =>
    this.pagedGroups().map((item) => ({
      id: item.bookingGroupId,
      bookingGroup_name: item.bookingGroup_name,
      generalLedger_name: item.generalLedger_name,
      startDate: formatDisplayDate(item.startDate, 'nl-NL'),
      endDate: this.formatEndDate(item.endDate),
    })),
  );

  private formatEndDate(endDate: BookingGroup['endDate']): string {
    const formatted = formatDisplayDate(endDate, 'nl-NL');
    return formatted === '31-12-9999' ? 'n.v.t' : formatted;
  }

  constructor() {
    this.reload$
      .pipe(
        switchMap(() => {
          this.isLoading.set(true);
          this.bookingGroups.set([]);
          return this.bookingGroupService.getByContext().pipe(
            catchError(() => {
              this.isLoading.set(false);
              this.bookingGroups.set([]);
              return EMPTY;
            }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (items) => {
          this.bookingGroups.set(items);
          this.pageIndex.set(0);
          this.isLoading.set(false);
        },
      });
  }

  ngOnInit(): void {
    this.loadBookingGroups();
  }

  loadBookingGroups(): void {
    this.reload$.next();
  }

  navigateToCreate(): void {
    this.router.navigate(['/config/bookinggroup/editable']);
  }

  onTableRowClick(row: Record<string, unknown>): void {
    // Default action when clicking a row is "Bekijk details".
    this.viewItem(String(row['id']));
  }

  viewItem(id: string, event?: MouseEvent): void {
    event?.stopPropagation();
    this.router.navigate(['/config/bookinggroup/editable', id]);
  }

  handleActionClick(id: string, actionId: string): void {
    if (actionId === 'edit') {
      this.viewItem(id);
      return;
    }
    if (actionId === 'bookingdefinition') {
      this.router.navigate(['/config/bookingdefinition', id]);
      return;
    }
    if (actionId === 'bookingrules') {
      this.router.navigate(['/config/bookingrule', id]);
      return;
    }
    if (actionId === 'delete') {
      this.deleteItem(id);
      return;
    }
  }

  private deleteItem(id: string): void {
    const bookingGroupName =
      this.bookingGroups().find((g) => g.bookingGroupId === id)?.bookingGroup_name ?? '';

    deleteBookingGroupWithChecks({
      id,
      bookingGroupName,
      service: this.bookingGroupService,
      dialogService: this.dialogService,
      destroyRef: this.destroyRef,
      onDeleted: () => this.loadBookingGroups(),
    });
  }
}
